// 图片存储优化服务
// 解决方案：只存储图片URL，不存储base64数据
// 对于豆包生成的图片，URL本身就是可访问的，无需存储base64

class ImageStorageService {
  constructor() {
    this.imageCache = new Map() // 内存缓存
    this.maxCacheSize = 50 // 最多缓存50张图片
  }

  /**
   * 优化图片存储 - 只存储URL，不存储base64
   * @param {string} imageUrl - 图片URL
   * @returns {string} - 优化后的URL
   */
  optimizeImageForStorage(imageUrl) {
    if (!imageUrl) return ''

    // 如果是base64数据，转换为Blob URL（仅在内存中）
    if (imageUrl.startsWith('data:image/')) {
      console.warn('⚠️ 检测到base64图片，建议使用URL存储')
      // 可以选择转换为Blob URL或保持原样
      // 但不应该存储到localStorage
      return imageUrl
    }

    // 如果是豆包或其他在线URL，直接返回
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl
    }

    return imageUrl
  }

  /**
   * 清理故事数据中的图片base64数据
   * @param {Object} storyData - 故事数据
   * @returns {Object} - 清理后的故事数据
   */
  cleanStoryImages(storyData) {
    if (!storyData) return storyData

    const cleanData = { ...storyData }

    // 清理节点中的图片
    if (cleanData.nodes && Array.isArray(cleanData.nodes)) {
      cleanData.nodes = cleanData.nodes.map(node => {
        if (node.data) {
          const cleanNode = { ...node }
          
          // 如果图片是base64，只保留URL引用
          if (node.data.sceneImage && node.data.sceneImage.startsWith('data:image/')) {
            console.log('🧹 清理节点中的base64图片:', node.id)
            cleanNode.data = {
              ...node.data,
              sceneImage: '', // 清空base64数据
              sceneImageUrl: node.data.sceneImageUrl || node.data.sceneImage // 保留URL
            }
          }
          
          return cleanNode
        }
        return node
      })
    }

    // 清理场景中的图片
    if (cleanData.scenes && Array.isArray(cleanData.scenes)) {
      cleanData.scenes = cleanData.scenes.map(scene => {
        if (scene.image && scene.image.startsWith('data:image/')) {
          console.log('🧹 清理场景中的base64图片:', scene.id || scene.name)
          return {
            ...scene,
            image: '', // 清空base64
            imageUrl: scene.imageUrl || scene.image // 保留URL
          }
        }
        if (scene.sceneImage && scene.sceneImage.startsWith('data:image/')) {
          return {
            ...scene,
            sceneImage: '',
            sceneImageUrl: scene.sceneImageUrl || scene.sceneImage
          }
        }
        return scene
      })
    }

    // 清理角色中的图片
    if (cleanData.characters && Array.isArray(cleanData.characters)) {
      cleanData.characters = cleanData.characters.map(character => {
        if (character.avatar && character.avatar.startsWith('data:image/')) {
          console.log('🧹 清理角色中的base64图片:', character.id || character.name)
          return {
            ...character,
            avatar: '',
            avatarUrl: character.avatarUrl || character.avatar
          }
        }
        if (character.image && character.image.startsWith('data:image/')) {
          return {
            ...character,
            image: '',
            imageUrl: character.imageUrl || character.image
          }
        }
        if (character.generatedImage && character.generatedImage.startsWith('data:image/')) {
          return {
            ...character,
            generatedImage: '',
            generatedImageUrl: character.generatedImageUrl || character.generatedImage
          }
        }
        return character
      })
    }

    return cleanData
  }

  /**
   * 计算数据大小（字节）
   * @param {any} data - 要计算的数据
   * @returns {number} - 数据大小（字节）
   */
  calculateDataSize(data) {
    try {
      const jsonString = JSON.stringify(data)
      return new Blob([jsonString]).size
    } catch (error) {
      console.error('计算数据大小失败:', error)
      return 0
    }
  }

  /**
   * 格式化数据大小显示
   * @param {number} bytes - 字节数
   * @returns {string} - 格式化后的字符串
   */
  formatSize(bytes) {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i]
  }

  /**
   * 检查存储空间并提供优化建议
   * @param {Object} data - 要存储的数据
   * @returns {Object} - 检查结果和建议
   */
  checkStorageSpace(data) {
    const dataSize = this.calculateDataSize(data)
    const maxSize = 5 * 1024 * 1024 // 5MB localStorage限制
    
    const result = {
      size: dataSize,
      formattedSize: this.formatSize(dataSize),
      maxSize: maxSize,
      percentage: (dataSize / maxSize * 100).toFixed(2),
      isOverLimit: dataSize > maxSize,
      needsOptimization: dataSize > maxSize * 0.8, // 超过80%就需要优化
      suggestions: []
    }

    if (result.isOverLimit) {
      result.suggestions.push('数据已超出存储限制，必须清理')
      result.suggestions.push('建议删除不必要的故事或场景')
      result.suggestions.push('图片应使用URL而非base64存储')
    } else if (result.needsOptimization) {
      result.suggestions.push('存储空间接近限制，建议优化')
      result.suggestions.push('清理base64图片数据')
      result.suggestions.push('压缩或删除旧数据')
    }

    return result
  }

  /**
   * 从localStorage获取当前使用情况
   * @returns {Object} - 存储使用情况
   */
  getStorageUsage() {
    try {
      let totalSize = 0
      const items = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        const value = localStorage.getItem(key)
        const size = new Blob([value]).size
        totalSize += size

        items.push({
          key,
          size,
          formattedSize: this.formatSize(size)
        })
      }

      // 按大小排序
      items.sort((a, b) => b.size - a.size)

      return {
        totalSize,
        formattedTotalSize: this.formatSize(totalSize),
        itemCount: localStorage.length,
        items: items.slice(0, 10), // 只返回前10个最大的项
        percentage: (totalSize / (5 * 1024 * 1024) * 100).toFixed(2)
      }
    } catch (error) {
      console.error('获取存储使用情况失败:', error)
      return {
        totalSize: 0,
        formattedTotalSize: '0 B',
        itemCount: 0,
        items: [],
        percentage: 0
      }
    }
  }

  /**
   * 智能清理存储空间
   * @param {Object} options - 清理选项
   * @returns {Object} - 清理结果
   */
  smartCleanup(options = {}) {
    const {
      removeBase64 = true,        // 移除base64图片
      removeLargeItems = true,    // 移除大型数据项
      removeOldData = true,        // 移除旧数据
      keepRecent = 5              // 保留最近的N个故事
    } = options

    const cleaned = {
      base64Removed: 0,
      largeItemsRemoved: 0,
      oldDataRemoved: 0,
      totalSizeFreed: 0
    }

    try {
      // 1. 清理base64图片数据
      if (removeBase64) {
        const storiesKey = 'altverse-stories'
        const storiesData = localStorage.getItem(storiesKey)
        if (storiesData) {
          try {
            const stories = JSON.parse(storiesData)
            const beforeSize = new Blob([storiesData]).size
            
            // 清理每个故事中的base64图片
            const cleanedStories = stories.map(story => this.cleanStoryImages(story))
            
            const cleanedData = JSON.stringify(cleanedStories)
            const afterSize = new Blob([cleanedData]).size
            
            localStorage.setItem(storiesKey, cleanedData)
            
            const saved = beforeSize - afterSize
            cleaned.totalSizeFreed += saved
            cleaned.base64Removed = saved
            
            console.log(`✅ 清理base64图片，释放空间: ${this.formatSize(saved)}`)
          } catch (error) {
            console.error('清理base64图片失败:', error)
          }
        }
      }

      // 2. 移除超大数据项（超过1MB）
      if (removeLargeItems) {
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && key.startsWith('altverse-') === false) { // 不删除核心数据
            const value = localStorage.getItem(key)
            const size = new Blob([value]).size
            if (size > 1024 * 1024) { // 1MB
              keysToRemove.push({ key, size })
            }
          }
        }

        keysToRemove.forEach(item => {
          localStorage.removeItem(item.key)
          cleaned.totalSizeFreed += item.size
          cleaned.largeItemsRemoved++
          console.log(`🗑️ 删除大型数据项: ${item.key} (${this.formatSize(item.size)})`)
        })
      }

      // 3. 清理旧数据
      if (removeOldData) {
        const oldKeys = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && (key.startsWith('user-edited-') || key.startsWith('ai-story-'))) {
            oldKeys.push(key)
          }
        }

        oldKeys.forEach(key => {
          const value = localStorage.getItem(key)
          const size = new Blob([value || '']).size
          localStorage.removeItem(key)
          cleaned.totalSizeFreed += size
          cleaned.oldDataRemoved++
        })

        if (oldKeys.length > 0) {
          console.log(`🗑️ 清理旧数据: ${oldKeys.length} 项`)
        }
      }

      console.log('✅ 智能清理完成:', {
        ...cleaned,
        totalSizeFreed: this.formatSize(cleaned.totalSizeFreed)
      })

      return cleaned
    } catch (error) {
      console.error('智能清理失败:', error)
      return cleaned
    }
  }

  /**
   * 将base64图片转换为Blob URL（仅用于显示）
   * @param {string} base64Data - base64数据
   * @returns {string} - Blob URL
   */
  base64ToBlobUrl(base64Data) {
    if (!base64Data || !base64Data.startsWith('data:image/')) {
      return base64Data
    }

    try {
      // 从缓存中获取
      if (this.imageCache.has(base64Data)) {
        return this.imageCache.get(base64Data)
      }

      // 转换为Blob
      const arr = base64Data.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      const blob = new Blob([u8arr], { type: mime })
      const blobUrl = URL.createObjectURL(blob)

      // 缓存管理
      if (this.imageCache.size >= this.maxCacheSize) {
        // 删除最早的缓存项
        const firstKey = this.imageCache.keys().next().value
        const firstValue = this.imageCache.get(firstKey)
        URL.revokeObjectURL(firstValue)
        this.imageCache.delete(firstKey)
      }

      this.imageCache.set(base64Data, blobUrl)
      return blobUrl
    } catch (error) {
      console.error('base64转Blob URL失败:', error)
      return base64Data
    }
  }

  /**
   * 清理Blob URL缓存
   */
  clearBlobCache() {
    this.imageCache.forEach(url => {
      URL.revokeObjectURL(url)
    })
    this.imageCache.clear()
    console.log('✅ 已清理Blob缓存')
  }
}

// 导出单例
const imageStorageService = new ImageStorageService()
export default imageStorageService


